import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/AuthProvider.tsx';
import { CollarStrip, Wordmark } from '@/components/Brand.tsx';
import { LanguagePicker } from '@/components/LanguagePicker.tsx';
import { Button } from '@/components/ui.tsx';

/**
 * The only screen a signed-out user can reach.
 *
 * Nothing here reads a role, a membership or a tenant. Sign-in is the one path
 * where a mistake cannot be fixed from inside the app — there is no session to
 * fix it with — so it does the minimum and hands off.
 *
 * There is one provider and no second column of alternatives, because there is
 * genuinely one provider: Google is what `pd provision` enables, and the invite
 * flow depends on a verified email address, which Google always supplies and a
 * password account does not until someone clicks a link in their inbox.
 */
export const SignIn = () => {
  const { t } = useTranslation();
  const { signInWithGoogle, signingIn, failed } = useAuth();

  return (
    <div className="flex min-h-dvh flex-col justify-center px-6 py-10">
      <div className="mx-auto w-full max-w-sm">
        <CollarStrip className="mb-8" />
        <h1 className="text-4xl leading-none">
          <Wordmark />
        </h1>
        {/* ~46 characters a line at this width: comfortably inside the 80 the
            eye can track without losing its place. */}
        <p className="mt-4 max-w-[32ch] text-lg leading-snug text-ink-soft">{t('auth.tagline')}</p>

        <div className="mt-10">
          <Button full onClick={() => void signInWithGoogle()} disabled={signingIn}>
            <GoogleMark />
            {signingIn ? t('auth.signingIn') : t('auth.signIn')}
          </Button>

          {failed && (
            <p role="alert" className="mt-3 text-sm text-alert">
              {t('auth.failed')}
            </p>
          )}
        </div>

        {/*
          Under the button rather than above it, and quiet: for almost everyone
          the language is already right and this is furniture they should read
          past. It has to be on this screen all the same — it is the last
          screen before a wall of Polish, and someone who does not read Polish
          has no way back from the other side of it.
        */}
        <LanguagePicker className="mt-8" />
      </div>
    </div>
  );
};

/**
 * Google's own mark, inline. A remote image here would be a network request on
 * the one screen that has to render for a user who has just installed the app
 * on a bad connection — and the brand guidelines require the four colours, so
 * the paths are the asset.
 */
const GoogleMark = () => (
  <svg viewBox="0 0 48 48" className="size-5 shrink-0" aria-hidden focusable="false">
    <path
      fill="#4285F4"
      d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
    />
    <path
      fill="#34A853"
      d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
    />
    <path
      fill="#FBBC05"
      d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
    />
    <path
      fill="#EA4335"
      d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
    />
  </svg>
);
